/**
 * @documental
 * @export PageTitle
 * @import
 * import { PageTitle } from "capitalrx-components-2.0/lib/typography";
 * @use
 * The `PageTitle` component should be used right at the top of your page, typically within the top Action Bar, should your page be
 * using one, and not as a part of your body content. Body content titles would instead be [Title](../Title/README.md),
 * [Subtitle](../Subtitle/README.md), [Heading](../Heading/README.md) and [Subheading](../Subheading/README.md).
 * @example
 * import { FC } from "react";
 * import { PageTitle, Paragraph } from "capitalrx-components-2.0/lib/typography";
 *
 * export const Component: FC = () => {
 *   return (
 *     <div>
 *       <PageTitle>Custom Page Title</PageTitle>
 *       <Paragraph>
 *         The quick brown fox jumps over the lazy dog.
 *       </Paragraph>
 *     </div>
 *   );
 * };
 * @output
 * <div>
 *   <h1 class="page-title"><span class="semibold">Custom Page Title</span></h1>
 *   <p class="paragraph">
 *     The quick brown fox jumps over the lazy dog.
 *   </p>
 * </div>
 */
import { FC, PropsWithChildren } from "react";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Semibold } from "../Semibold";
import { TextAlignment } from "../TextAlignment";

import styles from "./styles.module.scss";

/**
 * The PageTitle component properties.
 */
export interface PageTitleProps extends BaseProps {
  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;
}

/**
 * Displays page title text. The page title is automatically spaced apart from its siblings. However, if the title is the
 * first element it will not have any spacing above, and if it is the last element it will not have any spacing below.
 * Additionally, the page title is unaffected by text sizing applied to the [Body](../Body/README.md) component.
 */
export const PageTitle: FC<PropsWithChildren<PageTitleProps>> = (props) => {
  const { children, textAlign = "left", testId } = props;
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.PageTitle, "page-title", `align-${textAlign}`] });

  return (
    <h1 {...baseHtmlProps} data-testid={testId}>
      <Semibold>{children}</Semibold>
    </h1>
  );
};
